import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoEdit from '../../modal/InfoEdit';
import { useAppSelector } from '../../hooks/store';
import { Attendee, Language } from '../../types';
import { get } from '../../utils/api';
import { getCountryName } from '../../utils/countries';
import { getProductsType } from '../../utils/attendee';
import './Info.css';

const GEO_RU = { federal: 'Федеральная', regional: 'Региональная' };
const GEO_EN = { federal: 'Federal', regional: 'Regional' };

export interface InfoProps {
    attendee: Attendee;
};

const Info: FC<InfoProps> = ({ attendee }) => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const city = i18n.language === 'en' ? 'city_en' : 'city_ru';
    const company = i18n.language === 'en' ? 'company_en' : 'company_ru';
    const position = i18n.language === 'en' ? 'position_en' : 'position_ru';
    const description = i18n.language === 'en' ? 'description_en' : 'description_ru';
    const geography = i18n.language === 'en' ? GEO_EN : GEO_RU;
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const [ isEditOpen, setIsEditOpen ] = useState(false);
    const [ canEdit, setCanEdit ] = useState(false);
	const [ languages, setLanguages ] = useState<Language[]>([]);

	// Запрашиваем список языков
	useEffect(() => {
		const fetchLanguages = async (event_id:number) => {
			setLanguages([]);
			const result = await get('/langtoevent', { event_id });
            if(!result.data) return;
			const langs = (result.data.languages as Language[]).filter(lang => attendee.languages.includes(lang.id));
			setLanguages(langs);
		};

		fetchLanguages(attendee.event_id);
	}, [ attendee, name ]);

    // Вычисляем можем ли еще редактировать профайл
    useEffect(() => {
        if(!user || !event) return;
        if(attendee.id !== user.id) return;
        if(!event.editing_until) {
            setCanEdit(true);
            return;
        }
        const today = Date.now() + 3 * 60 * 60 * 1000; // +3 часа
        const until = Date.parse(event.editing_until);
        if(today <= until) setCanEdit(true);
    }, [ user, event, attendee ]);

    return (
        <div className='Info'>
            <table>
                <tbody>
                    {attendee[company] && <tr>
                        <td>{t('attendee.company')}</td>
                        <td>{attendee[company]}</td>
                    </tr>}
                    {attendee[position] && <tr>
                        <td>{t('attendee.position')}</td>
                        <td>{attendee[position]}</td>
                    </tr>}
                    {attendee.country && <tr>
                        <td>{t('attendee.country')}</td>
                        <td>{getCountryName(attendee.country, lang)}</td>
                    </tr>}
                    {attendee[city] && <tr>
                        <td>{t('attendee.city')}</td>
                        <td>{attendee[city]}</td>
                    </tr>}
                    {geography[attendee.geography] && <tr>
                        <td>{t('attendee.geography')}</td>
                        <td>{geography[attendee.geography]}</td>
                    </tr>}
                    {languages.length > 0 && <tr>
                        <td>{t('attendee.languages')}</td>
                        <td>{languages.map(l => l[name]).join(', ')}</td>
                    </tr>}
                    {(event?.use_direct_import && attendee.role === 'seller' && attendee.country !== 'RU') && <tr>
                        <td>{t('attendee.direct_import')}</td>
                        <td>{attendee.direct_import ? t('attendee.direct_import_true') : t('attendee.direct_import_false')}</td>
                    </tr>}
                    {(event?.use_products_type) && <tr>
                        <td>{t(`attendee.${attendee.role}.products_type`)}:</td>
                        <td>{getProductsType(attendee, t)}</td>
                    </tr>}
                </tbody>
            </table>
            {attendee[description]?.split('\n').map((line, index) => <span key={index}>{line}<br/></span>)}

            {canEdit && <>
                <p><span className='Link' onClick={() => setIsEditOpen(true)}>{t('profile.editprofile')}</span></p>
                <InfoEdit isOpen={isEditOpen} onClose={() => setIsEditOpen(false)}/>
            </>}
        </div>
	);
};

export default Info;
